import React, { useEffect, useRef, useState } from 'react';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { AppBar, Box, Button, createTheme, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useApi } from '../../../../services/HttpService';
import ScrollableDataGrid from '../../../../components/tables/ScrollableDataGrid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import BaseModal from '../../../../components/modals/BaseModal';
import { StickyAlert } from '../../../../components/ui/StickyAlert';
import { highlightedGridColumns } from '../../../../components/tables/BaseTable';
// import { EmailSetting, Contact, newEmailSetting, Organization } from '../../../../model';
import { EmailSetting, newEmailSetting, EmailProvider, newEmailProvider } from '../../../../model/Email';
import { EnhancedTableToolbar } from '../../../../components/tables/AdminTable';
import HardDeleteDialog from '../../../../components/modals/dialog/HardDeleteDialog';
import { EmailSettingOverview } from '../email/EmailSettingOverview';

const mainColumns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'name', headerName: 'Navn', width: 250 },
  { field: 'username', headerName: 'Brukernavn', width: 200 },
  { field: 'password', headerName: 'Passord', width: 150 },
  { field: 'sys_email_provider', headerName: 'E-post leverandør', width: 150 },
  { field: 'auth_by_user', headerName: 'Brukerautorisering', width: 200 , type: 'boolean'},
];

export const ManageEmailSettings = () => {
  const theme = useTheme();
  const { api, isLoading, error } = useApi();
  const { api: deleteApi, isLoading: deleteIsLoading, error: deleteError } = useApi();
  const { api: dataApi, error: dataError } = useApi();
  const [items, setItems] = useState<EmailSetting[]>([]);
  const [selectedItem, setSelectedItem] = useState<EmailSetting>(newEmailSetting());
  const [EmailProviders, setEmailProvider] = useState<EmailProvider[]>([]);
  const [filteredItems, setFilteredItems] = useState<EmailSetting[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly GridRowId[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEditItem, setOpenEditItem] = useState(false);
  
  const apiRef = useRef<GridApiCommunity>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    api("/manage/email/getEmailSettings", "POST", { search: searchText, limit: 0 }).then((res) => {
      if (res?.statusCode === 200) {
        setItems(res.data);
      }
    });

    api("/manage/email/getEmailProviders", "POST", { search: searchText, limit: 0 }).then((res) => {
      if (res?.statusCode === 200) {
        setEmailProvider(res.data);
      }
    });
  }

  useEffect(() => {
    handleSearch(searchText);
  }, [items]);

  // useEffect(() => {
  //   getItems();
  // }, [searchText]);

  const handleSearch = (searchText: string) => {
    const query = searchText.toLowerCase();
    setSearchText(query);

    const filteredData = items ? items.filter((row) =>
      mainColumns.some((column) => {

        // Use valueGetter if defined, otherwise use the field directly
        const value = column.valueGetter
          ? column.valueGetter(undefined as never, row, column, apiRef)
          : row[column.field];

        return String(value).toLowerCase().includes(query);
      })
    ) : [];

    setFilteredItems(filteredData);
  };

  const getPageItems = (page: number, pageSize: number) => {
    if (items.length === 0) {
      return [];
    }
    const sublist = items.slice(page * pageSize, (page + 1) * pageSize);
    console.log("returning comapnies from index", page * pageSize, "-", (page + 1) * pageSize, "count", sublist.length)
    return sublist;
  }

  const onItemChange = (changedItem: EmailSetting) => {
    setItems((prevItems) => {
      const index = prevItems.findIndex(u => u.id === changedItem.id);
      if (index > -1) {
        return prevItems.map((item, i) => (i === index ? changedItem : item));
      } else {
        return [...prevItems, changedItem];
      }
    });

    setSelectedItem(changedItem);
    setSelectedIds([changedItem.id]);
  }

  const handleRowSelectionModelChange = (ids: number[]) => {
    setSelectedIds(ids);

    if (ids.length > 0) {
      let selectedItem = items.find(u => u.id === ids[0]);
      console.log("found item:", selectedItem)
      setSelectedItem(selectedItem);
    } else {
      setSelectedItem(newEmailSetting());
    }
  }

  const handleRowDoubleClick = (row) => {
    let ids = [row.id];
    handleRowSelectionModelChange(ids);
    setOpenEditItem(true);
  }

  const handleDelete = (hardDelete: boolean) => {
    alert("delete not implemented");
  }

  return (
    <>
      <BaseModal
        open={openEditItem}
        title={selectedItem.id > 0 ? "Rediger e-post instilling" : "Ny e-post instilling"}
        onClose={() => { setOpenEditItem(false) }}
        closeOnBackdropClick={false}
        showButtons={false}
      >
        <EmailSettingOverview onClose={() => setOpenEditItem(false) } item={selectedItem} onItemChange={onItemChange} EmailProviders={EmailProviders}/>
      </BaseModal>
      <HardDeleteDialog
        open={openDelete}
        bodyText={`Er du sikker på at du vil slette ${selectedItem.name}? Trykk på "SLETT" for å bekrefte.`}
        includeHardDelete={true}
        loading={deleteIsLoading}
        errorMessage={deleteError}
        onClose={() => setOpenDelete(false)}
        onConfirm={(hardDelete) => handleDelete(hardDelete)}
      />

      <StickyAlert message={error ? error : dataError ? dataError : null} severity="error" />

      <Box sx={{ m: 1, mt: 3, p: { xs: 1, md: 3 }, pt: 0, width: "100%" }}>
        <Paper elevation={3} sx={{ borderRadius: "4px 4px 0 0" }}>
          <EnhancedTableToolbar
            title={"E-post instillinger"}
            numSelected={openEditItem || selectedIds.length > 0 ? 1 : 0}
            selectTitle={selectedItem.id ? `${selectedItem.name}` : "Ny e-post instilling"}
            onAdd={() => setOpenEditItem(true)}
            onEdit={() => setOpenEditItem(true)}
            onCancel={() => { setOpenEditItem(false); setSelectedIds([]); setSelectedItem(newEmailSetting()) }}
            onDelete={() => setOpenDelete(true)}
            onSearch={handleSearch}
            onRefresh={getData}
          />
        </Paper>
        <ScrollableDataGrid
          height={700}
          rows={filteredItems}
          columns={mainColumns ? highlightedGridColumns(mainColumns, searchText, apiRef) : []}
          loading={isLoading}
          onPageRequest={getPageItems}
          checkboxSelection
          disableMultipleRowSelection
          onRowSelectionModelChange={(ids) => handleRowSelectionModelChange(ids)}
          onRowDoubleClick={(row) => handleRowDoubleClick(row)}
          rowSelectionModel={selectedIds}
          keepNonExistentRowsSelected={true}
        />
      </Box>
    </>
  )
}